import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createPackage, updatePackage } from '../api/packagesApi'

/**
 * Package Modal Component
 * Form for creating/editing payment packages
 */
export function PackageModal({ isOpen, onClose, package: pkg }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!pkg

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      price: '',
      month_count: '1',
      is_published: 'true',
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (pkg) {
      reset({
        name: pkg.name || '',
        price: pkg.price?.toString() || '',
        month_count: pkg.month_count?.toString() || '1',
        is_published: pkg.is_published ? 'true' : 'false',
      })
    } else {
      reset({
        name: '',
        price: '',
        month_count: '1',
        is_published: 'true',
      })
    }
  }, [pkg, reset])

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      const data = {
        name: formData.name,
        price: parseInt(formData.price, 10),
        month_count: parseInt(formData.month_count, 10),
        is_published: formData.is_published === 'true',
      }

      if (isEdit) {
        return updatePackage(pkg.id, data)
      } else {
        return createPackage(data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit
          ? t('packages.packageUpdated')
          : t('packages.packageCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('packages.packageSaveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('packages.editPackage') : t('packages.createPackage')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Legend fontSize="md" fontWeight="600">
              {t('packages.packageInfo')}
            </Fieldset.Legend>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('packages.name')} *`}
                      placeholder={t('packages.namePlaceholder')}
                      error={errors.name?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="price"
                  control={control}
                  rules={{
                    required: t('validation.required'),
                    pattern: {
                      value: /^\d+$/,
                      message: t('validation.mustBeNumber'),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      type="number"
                      label={`${t('packages.price')} *`}
                      placeholder={t('packages.pricePlaceholder')}
                      error={errors.price?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="month_count"
                  control={control}
                  rules={{
                    required: t('validation.required'),
                    pattern: {
                      value: /^\d+$/,
                      message: t('validation.mustBeNumber'),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      type="number"
                      label={`${t('packages.monthCount')} *`}
                      placeholder={t('packages.monthCountPlaceholder')}
                      error={errors.month_count?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="is_published"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('packages.published')}
                      placeholder=""
                      options={[
                        { value: 'true', label: t('common.yes') },
                        { value: 'false', label: t('common.no') },
                      ]}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              isLoading={saveMutation.isPending}
            >
              {isEdit ? t('common.save') : t('common.create')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}

export default PackageModal
